"use client";
import { useEffect, useState, type RefObject } from "react";

export type HorizontalOverflow = "none" | "start" | "end" | "both";

/** Which edges of a sideways-scrolling element still hide content. */
export function horizontalOverflow(scrollLeft: number, scrollWidth: number, clientWidth: number): HorizontalOverflow {
  const hidden = scrollWidth - clientWidth;
  if (hidden <= 1) return "none";
  const before = scrollLeft > 1;
  const after = scrollLeft < hidden - 1;
  return before && after ? "both" : before ? "start" : "end";
}

/*
 * Tracks which edges of a sideways scroller still hide content, for its
 * data-overflow attribute: .mlx-scroll-fade[data-overflow] in globals.css
 * fades those edges so the row reads as scrollable.
 */
export function useHorizontalOverflow(ref: RefObject<HTMLElement | null>): HorizontalOverflow {
  const [overflow, setOverflow] = useState<HorizontalOverflow>("none");
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () =>
      setOverflow(horizontalOverflow(element.scrollLeft, element.scrollWidth, element.clientWidth));
    update();
    element.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);
    return () => {
      element.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [ref]);
  return overflow;
}
